'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';

import { readConsent, writeConsent } from '@/lib/consent';

const IDLE_TIMEOUT_MS = 1200;

export function CookieConsent() {
	const t = useTranslations('CookieConsent');
	const [visible, setVisible] = useState(false);
	const [entered, setEntered] = useState(false);

	useEffect(() => {
		if (readConsent() !== null) return;

		const reveal = () => {
			setVisible(true);
			requestAnimationFrame(() => setEntered(true));
		};

		const hasIdle = typeof window.requestIdleCallback === 'function';
		const handle = hasIdle
			? window.requestIdleCallback(reveal, { timeout: IDLE_TIMEOUT_MS })
			: window.setTimeout(reveal, IDLE_TIMEOUT_MS);

		return () => {
			if (hasIdle) {
				window.cancelIdleCallback(handle);
			} else {
				window.clearTimeout(handle);
			}
		};
	}, []);

	const decide = (value: 'accepted' | 'declined') => {
		writeConsent(value);
		setEntered(false);
		window.setTimeout(() => setVisible(false), 250);
	};

	if (!visible) return null;

	return (
		<div
			role="dialog"
			aria-modal="false"
			aria-labelledby="cookie-consent-title"
			aria-describedby="cookie-consent-desc"
			className={`border-border bg-surface fixed right-[calc(1rem+env(safe-area-inset-right))] bottom-[calc(1rem+env(safe-area-inset-bottom))] left-[calc(1rem+env(safe-area-inset-left))] z-[80] flex flex-col gap-5 border p-5 transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none sm:start-auto sm:end-[calc(1.5rem+env(safe-area-inset-right))] sm:bottom-[calc(1.5rem+env(safe-area-inset-bottom))] sm:max-w-md sm:p-6 ${entered ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-2 opacity-0'}`}
		>
			<div className="flex flex-col gap-3">
				<span id="cookie-consent-title" className="label-mono">
					<span className="text-muted">{t('titleCode')}</span> {t('titleText')}
				</span>
				<p
					id="cookie-consent-desc"
					className="text-body-sm text-accent leading-[1.6]"
				>
					{t('body')}
				</p>
			</div>

			<div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
				<button
					type="button"
					onClick={() => decide('declined')}
					className="text-nav tracking-nav text-accent border-border hover:text-fg hover:border-fg focus-visible:ring-fg flex min-h-11 items-center justify-center border bg-transparent px-5 font-mono uppercase transition-colors focus-visible:ring-1 focus-visible:outline-none"
				>
					{t('decline')}
				</button>
				<button
					type="button"
					onClick={() => decide('accepted')}
					className="text-nav tracking-nav text-bg bg-fg hover:bg-accent focus-visible:ring-fg flex min-h-11 items-center justify-center border border-transparent px-5 font-mono uppercase transition-colors focus-visible:ring-1 focus-visible:outline-none"
				>
					{t('accept')}
				</button>
			</div>
		</div>
	);
}
